/**
 * Body-specific category code mapping.
 *
 * NEET category codes vary across counselling bodies:
 *   MCC (AIQ)        uses OP, OBC, SC, ST, EWS, PH, …
 *   Karnataka (KEA)  uses GM, 1G, 2AG, 3AG, SCG, STG, …
 *   Kerala (CEE)     uses SM, EZ, MU, BH, SC, ST, …
 *   Maharashtra      uses OPEN, OPEN_W, NRI, IQ, OBC, SC, ST, EWS, …
 *   … and so on.
 *
 * This table maps our normalised, user-facing category codes (OP, OBC, SC, ST,
 * EWS, PWD) to the actual codes used in DB rows keyed by `counselling_body`.
 *
 * NOT EXHAUSTIVE — targeted at the "open / merit" pool for each caste bucket.
 * Reserved sub-tiers (e.g., Kannadiga vs Non-Kannadiga in Karnataka; rural in
 * Maharashtra) are omitted so an "OP" query stays realistic.
 *
 * If a mapping is missing for a body we fall back to `["OP", "OPEN", "GEN",
 * "UR"]` so the checker still returns a reasonable result.
 */

// Normalised label ↔ user-friendly display name (used as fallback options
// when a specific state's dropdown map isn't defined).
export const USER_CATEGORIES = [
  { code: "OP",  label: "Open / General" },
  { code: "OBC", label: "OBC" },
  { code: "EWS", label: "EWS" },
  { code: "SC",  label: "SC" },
  { code: "ST",  label: "ST" },
  { code: "PWD", label: "PWD" },
];

/**
 * State-aware Category dropdown options.
 *
 * Each state defines the ACTUAL categories used in its counselling — this lets
 * a Bihar candidate distinguish BC vs EBC, a Karnataka candidate pick their
 * exact caste tier (1 / 2A / 2B / 3A / 3B), a Kerala candidate pick their
 * community (Ezhava, Muslim, Latin…), etc.
 *
 * Each option is { value, label, userCategory?, stateCodes? }:
 *   • value            — unique key used by the <select>
 *   • label            — human-readable display
 *   • userCategory     — the underlying NEET social bucket
 *                        (OP/OBC/SC/ST/EWS/PWD) used for MCC AIQ matching
 *   • stateCodes       — if present, ONLY these state-body codes are matched
 *                        (overrides the full bucket for the state body).
 *
 * When a state isn't listed here, the six default `USER_CATEGORIES` are shown.
 */
export const DOMICILE_CATEGORY_OPTIONS = {
  "All India": [
    { value: "OP",  label: "Open / General", userCategory: "OP" },
    { value: "OBC", label: "OBC",            userCategory: "OBC" },
    { value: "EWS", label: "EWS",            userCategory: "EWS" },
    { value: "SC",  label: "SC",             userCategory: "SC" },
    { value: "ST",  label: "ST",             userCategory: "ST" },
    { value: "PWD", label: "PWD",            userCategory: "PWD" },
  ],
  "Bihar": [
    { value: "OP",       label: "Open (UR)",                                  userCategory: "OP" },
    { value: "OBC:BC",   label: "OBC · Backward Class (BC)",                  userCategory: "OBC", stateCodes: ["BC"] },
    { value: "OBC:EBC",  label: "OBC · Extremely Backward Class (EBC)",       userCategory: "OBC", stateCodes: ["EBC"] },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "EWS",      label: "EWS",                                         userCategory: "EWS" },
  ],
  "Karnataka": [
    { value: "OP",       label: "Open (General Merit)",                        userCategory: "OP" },
    { value: "OBC:CAT1", label: "OBC · Category 1",                            userCategory: "OBC", stateCodes: ["1G","1H","1K","1KH","1R","1RH"] },
    { value: "OBC:2A",   label: "OBC · Category 2A",                           userCategory: "OBC", stateCodes: ["2AG","2AH","2AK","2AKH","2AR","2ARH"] },
    { value: "OBC:2B",   label: "OBC · Category 2B (Muslim)",                  userCategory: "OBC", stateCodes: ["2BG","2BH","2BK","2BKH","2BR","2BRH"] },
    { value: "OBC:3A",   label: "OBC · Category 3A",                           userCategory: "OBC", stateCodes: ["3AG","3AH","3AK","3AKH","3AR","3ARH"] },
    { value: "OBC:3B",   label: "OBC · Category 3B",                           userCategory: "OBC", stateCodes: ["3BG","3BH","3BK","3BKH","3BR","3BRH"] },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "PWD",      label: "PWD",                                         userCategory: "PWD" },
  ],
  "Kerala": [
    { value: "OP",       label: "Open (State Merit)",                          userCategory: "OP" },
    { value: "OBC:EZ",   label: "OBC · Ezhava (EZ)",                           userCategory: "OBC", stateCodes: ["EZ"] },
    { value: "OBC:MU",   label: "OBC · Muslim (MU)",                           userCategory: "OBC", stateCodes: ["MU"] },
    { value: "OBC:BH",   label: "OBC · Backward Hindu (BH)",                   userCategory: "OBC", stateCodes: ["BH"] },
    { value: "OBC:LA",   label: "OBC · Latin Catholic (LA)",                   userCategory: "OBC", stateCodes: ["LA"] },
    { value: "OBC:DV",   label: "OBC · Dheevara (DV)",                         userCategory: "OBC", stateCodes: ["DV"] },
    { value: "OBC:VK",   label: "OBC · Viswakarma (VK)",                       userCategory: "OBC", stateCodes: ["VK"] },
    { value: "OBC:KN",   label: "OBC · Kudumbi (KN)",                          userCategory: "OBC", stateCodes: ["KN"] },
    { value: "OBC:BX",   label: "OBC · Other Muslim (BX)",                     userCategory: "OBC", stateCodes: ["BX"] },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "EWS",      label: "EWS",                                         userCategory: "EWS" },
  ],
  "Maharashtra": [
    { value: "OP",       label: "Open",                                        userCategory: "OP" },
    { value: "OBC",      label: "OBC",                                         userCategory: "OBC", stateCodes: ["OBC","OBC_W"] },
    { value: "OBC:SEBC", label: "OBC · SEBC (Maratha)",                        userCategory: "OBC", stateCodes: ["SEBC","SEBC_W","SOBC"] },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "EWS",      label: "EWS",                                         userCategory: "EWS" },
    { value: "PWD",      label: "PWD",                                         userCategory: "PWD" },
  ],
  "West Bengal": [
    { value: "OP",       label: "Open (UR)",                                   userCategory: "OP" },
    { value: "OBC:A",    label: "OBC · Category A",                            userCategory: "OBC", stateCodes: ["OBC_A"] },
    { value: "OBC:B",    label: "OBC · Category B",                            userCategory: "OBC", stateCodes: ["OBC_B"] },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "EWS",      label: "EWS",                                         userCategory: "EWS" },
    { value: "PWD",      label: "PWD",                                         userCategory: "PWD" },
  ],
  "Uttar Pradesh": [
    { value: "OP",       label: "Open (UR)",                                   userCategory: "OP" },
    { value: "OBC",      label: "OBC",                                         userCategory: "OBC" },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "EWS",      label: "EWS",                                         userCategory: "EWS" },
    { value: "PWD",      label: "PWD",                                         userCategory: "PWD" },
  ],
  "Telangana": [
    { value: "OP",       label: "Open (UR)",                                   userCategory: "OP" },
    { value: "OBC:BC_A", label: "OBC · BC-A",                                  userCategory: "OBC", stateCodes: ["BC_A"] },
    { value: "OBC:BC_B", label: "OBC · BC-B",                                  userCategory: "OBC", stateCodes: ["BC_B"] },
    { value: "OBC:BC_C", label: "OBC · BC-C",                                  userCategory: "OBC", stateCodes: ["BC_C"] },
    { value: "OBC:BC_D", label: "OBC · BC-D",                                  userCategory: "OBC", stateCodes: ["BC_D"] },
    { value: "OBC:BC_E", label: "OBC · BC-E (Muslim)",                         userCategory: "OBC", stateCodes: ["BC_E"] },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "EWS",      label: "EWS",                                         userCategory: "EWS" },
  ],
  "Andhra Pradesh": [
    { value: "OP",       label: "Open (OC)",                                   userCategory: "OP" },
    { value: "OBC:BC_A", label: "OBC · BC-A",                                  userCategory: "OBC", stateCodes: ["BC_A"] },
    { value: "OBC:BC_B", label: "OBC · BC-B",                                  userCategory: "OBC", stateCodes: ["BC_B"] },
    { value: "OBC:BC_C", label: "OBC · BC-C",                                  userCategory: "OBC", stateCodes: ["BC_C"] },
    { value: "OBC:BC_D", label: "OBC · BC-D",                                  userCategory: "OBC", stateCodes: ["BC_D"] },
    { value: "OBC:BC_E", label: "OBC · BC-E (Muslim)",                         userCategory: "OBC", stateCodes: ["BC_E"] },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "EWS",      label: "EWS",                                         userCategory: "EWS" },
  ],
  "Haryana": [
    { value: "OP",       label: "Open",                                        userCategory: "OP" },
    { value: "OBC:BC_A", label: "OBC · BC-A",                                  userCategory: "OBC", stateCodes: ["BC_A"] },
    { value: "OBC:BC_B", label: "OBC · BC-B",                                  userCategory: "OBC", stateCodes: ["BC_B"] },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "EWS",      label: "EWS",                                         userCategory: "EWS" },
  ],
  "Jharkhand": [
    { value: "OP",       label: "Open (UR)",                                   userCategory: "OP" },
    { value: "OBC:BC1",  label: "OBC · BC-I",                                  userCategory: "OBC", stateCodes: ["BC1"] },
    { value: "OBC:BC2",  label: "OBC · BC-II",                                 userCategory: "OBC", stateCodes: ["BC2"] },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "EWS",      label: "EWS",                                         userCategory: "EWS" },
  ],
  "Rajasthan": [
    { value: "OP",       label: "Open (GEN)",                                  userCategory: "OP" },
    { value: "OBC",      label: "OBC",                                         userCategory: "OBC", stateCodes: ["OBC"] },
    { value: "OBC:MBC",  label: "OBC · MBC",                                   userCategory: "OBC", stateCodes: ["MBC"] },
    { value: "SC",       label: "SC",                                          userCategory: "SC" },
    { value: "ST",       label: "ST",                                          userCategory: "ST" },
    { value: "EWS",      label: "EWS",                                         userCategory: "EWS" },
  ],
};

/**
 * Look up the category options for the given domicile. Returns the default
 * six categories when a state isn't in DOMICILE_CATEGORY_OPTIONS.
 */
export function getCategoryOptionsForDomicile(domicile) {
  return DOMICILE_CATEGORY_OPTIONS[domicile] || DOMICILE_CATEGORY_OPTIONS["All India"];
}

// User-state (from the domicile dropdown) ↔ internal counselling body name
// used inside the cutoffs collection. Only the states that RUN their own
// counselling are here; the rest fall through to MCC (All India Quota).
export const STATE_TO_BODY = {
  "Andhra Pradesh":  "Andhra_Pradesh",
  "Telangana":       "Telangana",
  "Karnataka":       "Karnataka",
  "Maharashtra":     "Maharashtra",
  "Kerala":          "Kerala",
  "Bihar":           "Bihar",
  "Uttar Pradesh":   "Uttar_Pradesh",
  "Rajasthan":       "Rajasthan",
  "Jharkhand":       "Jharkhand",
  "Haryana":         "Haryana",
  "West Bengal":     "West_Bengal",
  "Sikkim":          "Sikkim",
};

// Table: (counselling_body) × (user category) → list of body-specific codes
// that qualify. Codes match the strings we actually store in `counselling_results.category`.
// **Verified against distinct-value queries on the 2025 dataset.**
export const BODY_CATEGORY_CODES = {
  MCC: {
    OP:  ["OP"],
    OBC: ["BC"],
    SC:  ["SC"],
    ST:  ["ST"],
    EWS: ["EW"],
    PWD: ["OP_PH", "BC_PH", "SC_PH", "ST_PH", "EW_PH"],
  },
  Karnataka: {
    // General Merit + all its sub-domicile variants (Hyd-K, Kannada, Rural…)
    OP:  ["GM", "GMH", "GMK", "GMKH", "GMR", "GMRH"],
    OBC: ["1G", "1H", "1K", "1KH", "1R", "1RH",
          "2AG", "2AH", "2AK", "2AKH", "2AR", "2ARH",
          "2BG", "2BH", "2BK", "2BKH", "2BR", "2BRH",
          "3AG", "3AH", "3AK", "3AKH", "3AR", "3ARH",
          "3BG", "3BH", "3BK", "3BKH", "3BR", "3BRH"],
    SC:  ["SCG", "SCH", "SCK", "SCKH", "SCR", "SCRH", "S-G"],
    ST:  ["STG", "STH", "STK", "STKH", "STR", "STRH"],
    EWS: [],                                  // Karnataka has no EWS state pool
    PWD: ["PHM", "GMP", "GMPH"],
  },
  Kerala: {
    OP:  ["SM"],
    OBC: ["EZ", "MU", "BH", "LA", "DV", "VK", "KN", "BX"],
    SC:  ["SC"],
    ST:  ["ST"],
    EWS: ["EW"],
    PWD: [],
  },
  Maharashtra: {
    OP:  ["OPEN", "OPEN_W", "IQ", "NRI", "HA_OPEN"],
    OBC: ["OBC", "OBC_W", "SEBC", "SEBC_W", "SOBC"],
    SC:  ["SC", "SC_W"],
    ST:  ["ST", "ST_W"],
    EWS: ["EWS", "EWS_W"],
    PWD: ["PWD", "PWD_OPEN"],
  },
  West_Bengal: {
    OP:  ["UR", "MGMT_UR", "NRI_UR"],
    OBC: ["OBC", "OBC_A", "OBC_B"],
    SC:  ["SC"],
    ST:  ["ST"],
    EWS: ["EWS"],
    PWD: ["SC_PWD", "UR_PWD"],
  },
  Bihar: {
    OP:  ["UR"],
    OBC: ["BC", "EBC"],
    SC:  ["SC"],
    ST:  ["ST"],
    EWS: ["EWS"],
    PWD: [],
  },
  Uttar_Pradesh: {
    OP:  ["UROP"],
    OBC: ["BCOP"],
    SC:  ["SCOP"],
    ST:  ["STOP"],
    EWS: ["EWOP"],
    PWD: ["URPH", "BCPH", "SCPH", "EWPH"],
  },
  Telangana: {
    OP:  ["UR"],
    OBC: ["OBC", "BC_A", "BC_B", "BC_C", "BC_D", "BC_E"],
    SC:  ["SC"],
    ST:  ["ST"],
    EWS: ["EWS"],
    PWD: ["PH"],
  },
  Andhra_Pradesh: {
    OP:  ["OC", "CAT_A_OC", "CAT_B1_L", "CAT_B1_NL", "CAT_C_NRI"],
    OBC: ["BC_A", "BC_B", "BC_C", "BC_D", "BC_E"],
    SC:  ["SC"],
    ST:  ["ST"],
    EWS: ["EWS"],
    PWD: ["PH"],
  },
  Haryana: {
    OP:  ["OPEN_CAT", "MGT"],
    OBC: ["OBC", "BC_A", "BC_B"],
    SC:  ["SC"],
    ST:  ["ST"],
    EWS: ["EWS"],
    PWD: ["PH"],
  },
  Jharkhand: {
    OP:  ["UR"],
    OBC: ["BC1", "BC2"],
    SC:  ["SC"],
    ST:  ["ST"],
    EWS: ["EWS"],
    PWD: ["PH"],
  },
  Rajasthan: {
    OP:  ["GEN_URB", "GEN_URG", "GEN"],
    OBC: ["OBC", "MBC"],
    SC:  ["SC"],
    ST:  ["ST"],
    EWS: ["EWS"],
    PWD: ["PH"],
  },
  Sikkim: {
    OP:  ["GENERAL"],
    OBC: ["OBC"],
    SC:  ["SC"],
    ST:  ["ST"],
    EWS: ["EWS"],
    PWD: ["PH"],
  },
};

// Bodies that admit non-domicile students into their private/mgmt pool.
export const OPEN_MGMT_BODIES = new Set([
  "Karnataka", "Kerala", "Uttar_Pradesh", "Bihar", "West_Bengal", "Haryana",
  "Andhra_Pradesh", "Telangana",
]);

// Codes within OPEN_MGMT_BODIES that a non-domicile candidate can compete for.
export const OPEN_MGMT_CATEGORY = {
  Karnataka:      ["OPN", "NRI", "OTH"],
  Kerala:         ["SM"],
  Uttar_Pradesh:  ["UROP"],
  Bihar:          ["UR"],
  West_Bengal:    ["MGMT_UR", "NRI_UR"],
  Haryana:        ["OPEN_CAT", "MGT"],
  Andhra_Pradesh: ["CAT_B1_NL", "CAT_C_NRI"],
  Telangana:      ["UR_NL", "NRI", "NRI_MIN"],
};

/**
 * Build the exact list of `{counselling_body, category}` pairs that qualify
 * for a given (userCategory, domicileState, collegeState, stateCodesOverride).
 * Callers should use this to filter cutoff rows.
 *
 * Always includes MCC (All India Quota). Adds the college's own state body
 * codes when the student is a home-state domicile, or the open management-pool
 * codes when the student is a non-domicile going after private/mgmt seats.
 *
 * If `stateCodesOverride` is passed, it REPLACES the default state-body codes
 * for the user's category — used by state-aware sub-category dropdowns (e.g.
 * a Bihar candidate picks "OBC · EBC" and only rows with category == "EBC"
 * from the Bihar body are matched, instead of both BC and EBC).
 */
export function buildEligibleCategoryPairs({ userCategory, domicileState, collegeState, stateCodesOverride }) {
  const pairs = [];

  // MCC — every candidate gets All India Quota.
  const mccCodes = BODY_CATEGORY_CODES.MCC[userCategory] || BODY_CATEGORY_CODES.MCC.OP;
  mccCodes.forEach((cat) => pairs.push({ body: "MCC", category: cat }));

  if (!collegeState) return pairs;

  const collegeBody = STATE_TO_BODY[collegeState];
  if (!collegeBody) return pairs;

  const isHomeDomicile =
    domicileState && domicileState !== "All India"
    && domicileState.toLowerCase() === collegeState.toLowerCase();

  if (isHomeDomicile) {
    // Home-state candidate → their state body + user category.
    // If a sub-tier override was provided, use ONLY those codes; else the
    // full pool for the user's category.
    const codes = (stateCodesOverride && stateCodesOverride.length > 0)
      ? stateCodesOverride
      : (BODY_CATEGORY_CODES[collegeBody]?.[userCategory]
         || BODY_CATEGORY_CODES[collegeBody]?.OP
         || []);
    codes.forEach((cat) => pairs.push({ body: collegeBody, category: cat }));
  } else if (OPEN_MGMT_BODIES.has(collegeBody)) {
    // Non-domicile going after private/mgmt open pool of an "open" state.
    (OPEN_MGMT_CATEGORY[collegeBody] || []).forEach((cat) =>
      pairs.push({ body: collegeBody, category: cat }),
    );
  }

  return pairs;
}
